import { useEffect, useMemo, useRef, useState } from "react";

import type { MediaClip } from "../../api/media";
import { uploadFile } from "../../api/media";
import {
  fetchMusicCatalog, fetchSfxCatalog, importStock,
  searchWebMusic, getWebTrackUrl, importWebTrack,
  type StockMusicItem, type StockSfxItem, type WebSearchResult,
} from "../../api/stockAudio";
import { useEditor } from "../../store/editor";

export const MUSIC_DRAG_MIME = "application/x-videotool-music";

interface Props {
  projectId: number;
  library: MediaClip[];
  onRefresh: () => void;
  onClose: () => void;
}

type Tab = "catalog" | "web" | "sfx" | "uploads";

function fmtDur(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${rest.toString().padStart(2, "0")}`;
}

function fmtSize(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MusicPanel({ projectId, library, onRefresh, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("catalog");
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [musicCatalog, setMusicCatalog] = useState<StockMusicItem[]>([]);
  const [sfxCatalog, setSfxCatalog] = useState<StockSfxItem[]>([]);
  const [importing, setImporting] = useState(false);
  // Búsqueda web (Internet Archive)
  const [webResults, setWebResults] = useState<WebSearchResult[]>([]);
  const [webSearching, setWebSearching] = useState(false);
  const [webQuery, setWebQuery] = useState("");
  const [webUrlCache, setWebUrlCache] = useState<Record<string, string>>({});
  const addAudio = useEditor((s) => s.addAudioClip);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => { fetchMusicCatalog().then(setMusicCatalog).catch(() => {}); }, []);
  useEffect(() => { fetchSfxCatalog().then(setSfxCatalog).catch(() => {}); }, []);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const audios = useMemo(() => library.filter((c) => c.type === "audio" && c.status === "ready"), [library]);

  function togglePlay(url: string) {
    if (!url) return;
    if (playingUrl === url) {
      audioRef.current?.pause();
      setPlayingUrl(null);
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(url);
    a.volume = 0.6;
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingUrl(url);
    a.onended = () => { if (audioRef.current === a) setPlayingUrl(null); };
  }

  async function onUseStock(kind: "music" | "sfx", id: string, title: string) {
    setImporting(true);
    try {
      const clip = await importStock(projectId, kind, id);
      onRefresh();
      addAudio(clip.id, clip.duration_ms, title);
    } catch (e: any) {
      alert(`Error importando ${title}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setImporting(false);
    }
  }

  async function onSearchWeb() {
    if (!webQuery.trim()) return;
    setWebSearching(true);
    try {
      const results = await searchWebMusic(webQuery);
      setWebResults(results);
    } catch (e: any) {
      console.error(e);
      setWebResults([]);
    } finally {
      setWebSearching(false);
    }
  }

  async function onPlayWeb(item: WebSearchResult) {
    const cached = webUrlCache[item.id];
    if (cached) {
      togglePlay(cached);
      return;
    }
    try {
      const { url } = await getWebTrackUrl(item.id);
      setWebUrlCache((c) => ({ ...c, [item.id]: url }));
      togglePlay(url);
    } catch (e) {
      console.error(e);
    }
  }

  async function onUseWeb(item: WebSearchResult) {
    setImporting(true);
    try {
      const clip = await importWebTrack(projectId, item.id, item.title);
      onRefresh();
      addAudio(clip.id, clip.duration_ms, clip.name);
    } catch (e: any) {
      alert(`Error importando: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setImporting(false);
    }
  }

  async function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      const key = `${f.name}:${f.size}`;
      setUploading((p) => ({ ...p, [key]: 0 }));
      try {
        await uploadFile(projectId, f, (pct) => setUploading((p) => ({ ...p, [key]: pct })));
      } catch (err) { console.error(err); }
      finally {
        setUploading((p) => { const c = { ...p }; delete c[key]; return c; });
        onRefresh();
      }
    }
  }

  function onDragStartItem(e: React.DragEvent, mediaId: number, durationMs: number, name: string) {
    const payload = { mediaId, mediaType: "audio", durationMs, name };
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(MUSIC_DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData(`${MUSIC_DRAG_MIME}+id`, String(mediaId));
  }

  const filteredMusic = musicCatalog.filter((m) =>
    !query || m.title.toLowerCase().includes(query.toLowerCase()) || m.mood.toLowerCase().includes(query.toLowerCase()));
  const filteredSfx = sfxCatalog.filter((s) =>
    !query || s.title.toLowerCase().includes(query.toLowerCase()) || s.category.toLowerCase().includes(query.toLowerCase()));
  const filteredUploads = audios.filter((a) => !query || a.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div style={{
      position: "fixed", left: 0, top: 56, bottom: 0, width: 420,
      zIndex: 9000, pointerEvents: "none",
    }}>
      <div style={{
        width: "100%", height: "100%", background: "var(--bg-elev)",
        border: "1px solid var(--border)", boxShadow: "8px 0 30px rgba(0,0,0,0.4)",
        padding: 14, overflow: "hidden", display: "flex", flexDirection: "column",
        pointerEvents: "auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>🎵 Audio</h3>
          <input placeholder="Buscar…" value={query}
                 onChange={(e) => setQuery(e.target.value)} style={{ flex: 1, maxWidth: 160 }} />
          <button onClick={onClose} style={{ fontSize: 12 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 10,
                       borderBottom: "1px solid var(--border)", paddingBottom: 4 }}>
          {([["catalog", `🎶 Stock (${musicCatalog.length})`],
             ["web", "🌐 Buscar"],
             ["sfx", `🔊 SFX (${sfxCatalog.length})`],
             ["uploads", `📁 Tuyos (${audios.length})`]] as const).map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
                    style={{
                      flex: 1, fontSize: 11, padding: "6px 2px",
                      background: tab === key ? "#c084fc" : "var(--bg-elev-2)",
                      color: tab === key ? "#1a0738" : "var(--text)",
                      borderColor: tab === key ? "#c084fc" : "var(--border)",
                      fontWeight: tab === key ? 700 : 400,
                    }}>{label}</button>
          ))}
        </div>

        {tab === "uploads" && (
          <button onClick={() => inputRef.current?.click()}
                  style={{ background: "#c084fc", color: "#1a0738", borderColor: "#c084fc",
                           fontWeight: 700, padding: "8px 12px", marginBottom: 10 }}>
            + Subir audio (MP3 / WAV / M4A)
          </button>
        )}
        <input ref={inputRef} type="file" multiple accept="audio/*"
               style={{ display: "none" }} onChange={onPickFiles} />

        {Object.entries(uploading).map(([k, v]) => (
          <div key={k} style={{ padding: "4px 0", fontSize: 11, color: "var(--text-dim)" }}>
            ⬆ {k.split(":")[0]} — {v}%
          </div>
        ))}

        <div style={{ overflowY: "auto", flex: 1 }}>
          {tab === "catalog" && (
            <>
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 8,
                            padding: "6px 8px", background: "rgba(192,132,252,0.10)",
                            border: "1px solid rgba(192,132,252,0.3)", borderRadius: 4 }}>
                Música generada algorítmicamente por SoundHelix. Libre uso. Próximamente: Pixabay (100k+ tracks).
              </div>
              {filteredMusic.map((m) => (
                <StockRow
                  key={m.id}
                  title={m.title}
                  subtitle={`${m.mood}${m.bpm ? ` · ${m.bpm} BPM` : ""} · ${fmtDur(m.duration_ms)}`}
                  url={m.url}
                  isPlaying={playingUrl === m.url}
                  onPlay={() => togglePlay(m.url)}
                  onUse={() => onUseStock("music", m.id, m.title)}
                  busy={importing}
                  accent="#c084fc"
                />
              ))}
            </>
          )}
          {tab === "web" && (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input
                  placeholder="Buscar en Internet Archive…"
                  value={webQuery}
                  onChange={(e) => setWebQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onSearchWeb(); }}
                  style={{ flex: 1 }}
                />
                <button onClick={onSearchWeb} disabled={webSearching}
                        style={{ background: "#c084fc", color: "#1a0738",
                                 borderColor: "#c084fc", fontWeight: 600,
                                 padding: "6px 14px" }}>
                  {webSearching ? "…" : "Buscar"}
                </button>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 8,
                            padding: "6px 8px", background: "rgba(192,132,252,0.10)",
                            border: "1px solid rgba(192,132,252,0.3)", borderRadius: 4 }}>
                Busca en miles de audios públicos de Internet Archive (CC0 / dominio público).
                Prueba con: <em>piano</em>, <em>ambient</em>, <em>jazz</em>, <em>rock</em>, <em>orchestra</em>…
              </div>
              {webResults.length === 0 && !webSearching && webQuery && (
                <div style={{ textAlign: "center", color: "var(--text-dim)", fontSize: 12, padding: 20 }}>
                  Sin resultados. Prueba otros términos.
                </div>
              )}
              {webResults.map((it) => (
                <StockRow
                  key={it.id}
                  title={it.title}
                  subtitle={[it.creator, it.year].filter(Boolean).join(" · ") || "Internet Archive"}
                  url={webUrlCache[it.id] || ""}
                  isPlaying={!!webUrlCache[it.id] && playingUrl === webUrlCache[it.id]}
                  onPlay={() => onPlayWeb(it)}
                  onUse={() => onUseWeb(it)}
                  busy={importing}
                  accent="#c084fc"
                />
              ))}
            </>
          )}
          {tab === "sfx" && (
            <>
              {filteredSfx.map((s) => (
                <StockRow
                  key={s.id}
                  title={s.title}
                  subtitle={`${s.category} · ${fmtDur(s.duration_ms)}`}
                  url={s.url}
                  isPlaying={playingUrl === s.url}
                  onPlay={() => togglePlay(s.url)}
                  onUse={() => onUseStock("sfx", s.id, s.title)}
                  busy={importing}
                  accent="#c084fc"
                />
              ))}
            </>
          )}
          {tab === "uploads" && (
            <>
              {filteredUploads.length === 0 && (
                <div style={{ textAlign: "center", color: "var(--text-dim)", fontSize: 12, padding: 30 }}>
                  {audios.length === 0
                    ? "Aún no tienes audio. Sube un archivo arriba o explora Música / SFX."
                    : `Sin resultados para "${query}".`}
                </div>
              )}
              {filteredUploads.map((c) => (
                <StockRow
                  key={c.id}
                  title={c.name}
                  subtitle={`${fmtDur(c.duration_ms)} · ${fmtSize(c.size_bytes)}`}
                  url={c.original_url || c.proxy_url || ""}
                  isPlaying={playingUrl === (c.original_url || c.proxy_url || "")}
                  onPlay={() => togglePlay(c.original_url || c.proxy_url || "")}
                  onUse={() => addAudio(c.id, c.duration_ms, c.name)}
                  draggable
                  onDragStart={(e) => onDragStartItem(e, c.id, c.duration_ms, c.name)}
                  accent="#c084fc"
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StockRow(props: {
  title: string; subtitle: string; url: string;
  isPlaying: boolean; onPlay: () => void; onUse: () => void;
  busy?: boolean; draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  accent: string;
}) {
  return (
    <div
      draggable={props.draggable}
      onDragStart={props.onDragStart}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 10px", marginBottom: 4, borderRadius: 6,
        background: "var(--bg-elev-2)", border: "1px solid var(--border)",
        cursor: props.draggable ? "grab" : "default",
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = props.accent}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
    >
      <button onClick={props.onPlay}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                background: props.isPlaying ? props.accent : "var(--bg-elev)",
                color: props.isPlaying ? "#1a0738" : "var(--text)",
                padding: 0, fontSize: 14, lineHeight: 1, flexShrink: 0,
                display: "grid", placeItems: "center",
              }}>
        {props.isPlaying ? "❚❚" : "▶"}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text)",
                       whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {props.title}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-dim)" }}>{props.subtitle}</div>
      </div>
      <button onClick={props.onUse} disabled={props.busy}
              style={{ fontSize: 11, padding: "4px 8px",
                       background: props.accent, color: "#1a0738",
                       border: `1px solid ${props.accent}`, fontWeight: 600 }}>
        + Timeline
      </button>
    </div>
  );
}
